import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useTrackedEntities } from '../hooks/useTrackedEntities';
import { useRevisionTimeline } from '../hooks/useRevisionTimeline';
import { RevisionDiffDrawer } from '../components/RevisionDiffDrawer';
import { RevisionSnapshotDrawer } from '../components/RevisionSnapshotDrawer';
import { entityFilterSchema } from '../schema/changeHistoryViewer.schema';
import type { EntityFilterForm } from '../schema/changeHistoryViewer.schema';
import type {
  TrackedEntityListItem,
  RevisionTimelineItem,
  TrackedEntityStatus,
  RevisionType,
  ApiError,
} from '../types/changeHistoryViewer';
import type { StatusTone } from '@ui-kit';

// ─────────────────────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────────────────────

function statusTone(s: TrackedEntityStatus): StatusTone {
  if (s === 'PUBLISHED') return 'success';
  if (s === 'REVIEW') return 'warning';
  if (s === 'ARCHIVED') return 'neutral';
  return 'neutral';
}

function revTypeTone(t: RevisionType): StatusTone {
  if (t === 'ADD') return 'success';
  if (t === 'DEL') return 'danger';
  return 'warning';
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton row
// ─────────────────────────────────────────────────────────────────────────────

function SkeletonItem({ width = '70%' }: { width?: string }) {
  return (
    <div
      style={{
        height: 12,
        width,
        borderRadius: 'var(--radius-sm)',
        background: 'var(--color-surface-elevated)',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Entity panel skeleton
// ─────────────────────────────────────────────────────────────────────────────

function EntityPanelSkeleton() {
  return (
    <div
      style={{ padding: 'var(--spacing-2)', display: 'flex', flexDirection: 'column', gap: 2 }}
      aria-busy="true"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            padding: 'var(--spacing-3)',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
          }}
        >
          <SkeletonItem width={`${55 + (i * 11) % 35}%`} />
          <SkeletonItem width="30%" />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeline skeleton
// ─────────────────────────────────────────────────────────────────────────────

function TimelineSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      aria-busy="true"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
            display: 'flex',
            gap: 'var(--spacing-3)',
            alignItems: 'center',
          }}
        >
          <SkeletonItem width="60px" />
          <SkeletonItem width="120px" />
          <SkeletonItem width="100px" />
          <SkeletonItem width="40px" />
          <SkeletonItem width={`${40 + (i * 17) % 30}%`} />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const MAX_COMPARE_SELECT = 2;

/**
 * ChangeHistoryViewerPage — /change-history-viewer
 * 5상태: loading / empty / error / default / dialog(diff drawer + snapshot drawer)
 */
export function ChangeHistoryViewerPage() {
  const { t } = useTranslation();

  // ── 필터 form ──────────────────────────────────────────────────────────────
  const { register, handleSubmit, reset, watch } = useForm<EntityFilterForm>({
    resolver: zodResolver(entityFilterSchema),
    defaultValues: { keyword: '', status: '' },
  });

  const [appliedFilters, setAppliedFilters] = useState<EntityFilterForm>({
    keyword: '',
    status: '',
  });

  const onFilterSubmit = useCallback((data: EntityFilterForm) => {
    setAppliedFilters(data);
  }, []);

  const handleFilterReset = useCallback(() => {
    reset({ keyword: '', status: '' });
    setAppliedFilters({ keyword: '', status: '' });
  }, [reset]);

  // ── 선택된 엔티티 ──────────────────────────────────────────────────────────
  const [selectedEntity, setSelectedEntity] = useState<TrackedEntityListItem | null>(null);

  // ── 비교용 revision 선택 (최대 2개) ────────────────────────────────────────
  const [compareSelections, setCompareSelections] = useState<number[]>([]);

  const toggleCompareSelection = useCallback((rev: number) => {
    setCompareSelections((prev) => {
      if (prev.includes(rev)) return prev.filter((r) => r !== rev);
      if (prev.length >= MAX_COMPARE_SELECT) return [prev[1], rev]; // 오래된 것 제거
      return [...prev, rev];
    });
  }, []);

  // ── drawer 상태 ────────────────────────────────────────────────────────────
  const [diffDrawer, setDiffDrawer] = useState<{
    fromRev: number;
    toRev: number;
  } | null>(null);
  const [snapshotDrawer, setSnapshotDrawer] = useState<number | null>(null);

  const openDiff = useCallback((from: number, to: number) => {
    setSnapshotDrawer(null);
    setDiffDrawer({ fromRev: Math.min(from, to), toRev: Math.max(from, to) });
  }, []);

  const openCompareDiff = useCallback(() => {
    if (compareSelections.length === 2) {
      const [a, b] = compareSelections.sort((x, y) => x - y);
      openDiff(a, b);
    }
  }, [compareSelections, openDiff]);

  const openSnapshot = useCallback((rev: number) => {
    setDiffDrawer(null);
    setSnapshotDrawer(rev);
  }, []);

  // ── entities query (infinite) ──────────────────────────────────────────────
  const entitiesQuery = useTrackedEntities({
    keyword: appliedFilters.keyword || undefined,
    status: appliedFilters.status || undefined,
  });

  const allEntities = useMemo(
    () => entitiesQuery.data?.pages.flatMap((p) => p.content) ?? [],
    [entitiesQuery.data]
  );

  // 엔티티 목록 하단 sentinel (IntersectionObserver)
  const entitySentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!entitySentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entitiesQuery.hasNextPage && !entitiesQuery.isFetchingNextPage) {
          void entitiesQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(entitySentinelRef.current);
    return () => obs.disconnect();
  }, [entitiesQuery]);

  // ── timeline query (infinite) ─────────────────────────────────────────────
  const timelineQuery = useRevisionTimeline(selectedEntity?.id);

  const allRevisions = useMemo(
    () => timelineQuery.data?.pages.flatMap((p) => p.content) ?? [],
    [timelineQuery.data]
  );

  // 타임라인 하단 sentinel (IntersectionObserver)
  const timelineSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!timelineSentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          timelineQuery.hasNextPage &&
          !timelineQuery.isFetchingNextPage
        ) {
          void timelineQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(timelineSentinelRef.current);
    return () => obs.disconnect();
  }, [timelineQuery]);

  // 엔티티 변경 시 타임라인 초기화
  useEffect(() => {
    setCompareSelections([]);
    setDiffDrawer(null);
    setSnapshotDrawer(null);
  }, [selectedEntity?.id]);

  // ── 에러 메시지 ────────────────────────────────────────────────────────────
  const entitiesErrMsg = (() => {
    const e = entitiesQuery.error as unknown as ApiError | undefined;
    if (!e) return t('changeHistoryViewer.error.generic');
    if (e.code === 'AUTH-403-001') return t('changeHistoryViewer.error.forbidden');
    return e.message ?? t('changeHistoryViewer.error.generic');
  })();

  const timelineErrMsg = (() => {
    const e = timelineQuery.error as unknown as ApiError | undefined;
    if (!e) return t('changeHistoryViewer.error.generic');
    if (e.code === 'AUTH-403-001') return t('changeHistoryViewer.error.forbidden');
    if (e.code === 'CURSOR-400-001') return t('changeHistoryViewer.error.invalidCursor');
    if (e.code === 'CHGHIST-400-001') return t('changeHistoryViewer.error.entityNotFound');
    return e.message ?? t('changeHistoryViewer.error.generic');
  })();

  // ── 상태 플래그 ────────────────────────────────────────────────────────────
  const entitiesLoading = entitiesQuery.isLoading;
  const entitiesError = entitiesQuery.isError;
  const entitiesEmpty = !entitiesLoading && !entitiesError && allEntities.length === 0;

  const timelineLoading = timelineQuery.isLoading && !!selectedEntity;
  const timelineError = timelineQuery.isError;
  const timelineEmpty =
    !!selectedEntity &&
    !timelineLoading &&
    !timelineError &&
    allRevisions.length === 0;

  // ── 정렬된 비교 선택 ───────────────────────────────────────────────────────
  const sortedSelections = useMemo(
    () => [...compareSelections].sort((a, b) => a - b),
    [compareSelections]
  );

  const canCompare = sortedSelections.length === 2;

  // ── enter key on filter ────────────────────────────────────────────────────
  const keyword = watch('keyword');
  useEffect(() => {
    void keyword; // watch 구독 유지 (unused var 방지)
  }, [keyword]);

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('changeHistoryViewer.page.title')}
        breadcrumb={<span>{t('changeHistoryViewer.page.breadcrumb')}</span>}
        actions={
          canCompare && selectedEntity ? (
            <button
              type="button"
              onClick={openCompareDiff}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('changeHistoryViewer.action.compareSelected')} (rev {sortedSelections[0]} ↔ {sortedSelections[1]})
            </button>
          ) : undefined
        }
      />

      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: 'var(--spacing-1) 0 var(--spacing-3)',
        }}
      >
        {t('changeHistoryViewer.page.subtitle')}
      </p>

      {/* 비교 선택 알림 */}
      {compareSelections.length > 0 && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-brand-primary-subtle, rgba(98,0,238,0.06))',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
            {t('changeHistoryViewer.action.selectedCount', { count: compareSelections.length })}
            {compareSelections.length > 0 && (
              <> — rev {compareSelections.map(String).join(', ')}</>
            )}
          </span>
          {canCompare && (
            <button
              type="button"
              onClick={openCompareDiff}
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('changeHistoryViewer.action.compareSelected')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setCompareSelections([])}
            style={{
              padding: 'var(--spacing-1) var(--spacing-2)',
              background: 'transparent',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            {t('changeHistoryViewer.action.clearSelection')}
          </button>
        </div>
      )}

      {/* 2-panel 레이아웃 */}
      <div
        className="chv-layout"
        style={{
          display: 'grid',
          gridTemplateColumns: '280px 1fr',
          gap: 'var(--spacing-4)',
          alignItems: 'start',
        }}
      >
        {/* ── 왼쪽 패널: 추적 엔티티 목록 ── */}
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-background)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '80vh',
          }}
        >
          {/* 패널 제목 */}
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              borderBottom: '1px solid var(--color-surface-border)',
              background: 'var(--color-surface-muted)',
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('changeHistoryViewer.entities.title')}
            </h2>
          </div>

          {/* 필터 form */}
          <form
            onSubmit={(e) => { e.preventDefault(); void handleSubmit(onFilterSubmit)(e); }}
            style={{
              padding: 'var(--spacing-3)',
              borderBottom: '1px solid var(--color-surface-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-2)',
              flexShrink: 0,
            }}
          >
            <input
              {...register('keyword')}
              type="search"
              placeholder={t('changeHistoryViewer.entities.search.placeholder')}
              aria-label={t('changeHistoryViewer.entities.search.placeholder')}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-sans)',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <select
                {...register('status')}
                aria-label={t('changeHistoryViewer.entities.filter.status')}
                style={{
                  flex: 1,
                  padding: 'var(--spacing-2)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                }}
              >
                <option value="">{t('changeHistoryViewer.entities.filter.all')}</option>
                <option value="DRAFT">{t('changeHistoryViewer.status.DRAFT')}</option>
                <option value="REVIEW">{t('changeHistoryViewer.status.REVIEW')}</option>
                <option value="PUBLISHED">{t('changeHistoryViewer.status.PUBLISHED')}</option>
                <option value="ARCHIVED">{t('changeHistoryViewer.status.ARCHIVED')}</option>
              </select>
              <button
                type="submit"
                style={{
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                검색
              </button>
              <button
                type="button"
                onClick={handleFilterReset}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-2)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                }}
              >
                초기화
              </button>
            </div>
          </form>

          {/* 엔티티 목록 스크롤 영역 */}
          <div
            style={{ flex: 1, overflowY: 'auto' }}
            role="list"
            aria-label={t('changeHistoryViewer.entities.ariaLabel')}
            aria-busy={entitiesLoading}
          >
            {/* loading */}
            {entitiesLoading && <EntityPanelSkeleton />}

            {/* error */}
            {entitiesError && (
              <div style={{ padding: 'var(--spacing-4)' }}>
                <ErrorState
                  title={t('changeHistoryViewer.error.generic')}
                  message={entitiesErrMsg}
                  onRetry={() => void entitiesQuery.refetch()}
                />
              </div>
            )}

            {/* empty */}
            {entitiesEmpty && (
              <div style={{ padding: 'var(--spacing-4)' }}>
                <EmptyState
                  title={t('changeHistoryViewer.empty.noEntities')}
                  description={t('changeHistoryViewer.empty.noEntitiesDescription')}
                />
              </div>
            )}

            {/* default — 엔티티 행 */}
            {!entitiesLoading && !entitiesError && allEntities.length > 0 &&
              allEntities.map((entity) => {
                const isSelected = selectedEntity?.id === entity.id;
                return (
                  <div
                    key={entity.id}
                    role="listitem"
                    aria-current={isSelected ? 'true' : undefined}
                    aria-label={entity.name}
                    tabIndex={0}
                    onClick={() => setSelectedEntity(entity)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedEntity(entity);
                      }
                    }}
                    style={{
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      cursor: 'pointer',
                      background: isSelected
                        ? 'var(--color-brand-primary-subtle, rgba(98,0,238,0.06))'
                        : 'transparent',
                      borderLeft: isSelected
                        ? '3px solid var(--color-brand-primary)'
                        : '3px solid transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        (e.currentTarget as HTMLDivElement).style.background =
                          'var(--color-surface-muted)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 'var(--spacing-1)',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-primary)',
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entity.name}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontFamily: 'var(--font-family-mono)',
                          color: 'var(--color-brand-primary)',
                          fontWeight: 600,
                          marginLeft: 'var(--spacing-2)',
                          flexShrink: 0,
                        }}
                      >
                        {entity.revisionCount}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                      <StatusPill tone={statusTone(entity.status)}>
                        {t(`changeHistoryViewer.status.${entity.status}`)}
                      </StatusPill>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        P{entity.priority}
                      </span>
                    </div>
                  </div>
                );
              })
            }

            {/* 엔티티 무한 스크롤 sentinel */}
            <div ref={entitySentinelRef} style={{ height: 8 }} />
            {entitiesQuery.isFetchingNextPage && (
              <div
                style={{
                  padding: 'var(--spacing-2)',
                  textAlign: 'center',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t('changeHistoryViewer.entities.loadingMore')}
              </div>
            )}
            {!entitiesQuery.hasNextPage && allEntities.length > 0 && (
              <div
                style={{
                  padding: 'var(--spacing-2)',
                  textAlign: 'center',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {t('changeHistoryViewer.entities.end')}
              </div>
            )}
          </div>
        </div>

        {/* ── 오른쪽 패널: revision 타임라인 ── */}
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-background)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 400,
          }}
        >
          {/* 패널 헤더 */}
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              borderBottom: '1px solid var(--color-surface-border)',
              background: 'var(--color-surface-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {selectedEntity
                ? `${t('changeHistoryViewer.timeline.title')} — ${selectedEntity.name} (#${selectedEntity.id})`
                : t('changeHistoryViewer.timeline.title')}
            </h2>
            {allRevisions.length > 0 && (
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {allRevisions.length}
                {timelineQuery.hasNextPage ? '+' : ''} revisions
              </span>
            )}
          </div>

          {/* 타임라인 테이블 헤더 */}
          {(allRevisions.length > 0 || timelineLoading) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '80px 160px 130px 70px 1fr 160px',
                gap: 0,
                padding: 'var(--spacing-2) var(--spacing-4)',
                borderBottom: '1px solid var(--color-surface-border)',
                background: 'var(--color-surface-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                flexShrink: 0,
              }}
            >
              <span>{t('changeHistoryViewer.timeline.column.revision')}</span>
              <span>{t('changeHistoryViewer.timeline.column.time')}</span>
              <span>{t('changeHistoryViewer.timeline.column.actor')}</span>
              <span>{t('changeHistoryViewer.timeline.column.type')}</span>
              <span>{t('changeHistoryViewer.timeline.column.summary')}</span>
              <span style={{ textAlign: 'right' }}>{t('changeHistoryViewer.timeline.column.actions')}</span>
            </div>
          )}

          {/* 스크롤 영역 */}
          <div
            role="list"
            aria-label={t('changeHistoryViewer.timeline.ariaLabel')}
            aria-busy={timelineLoading}
            style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(80vh - 120px)' }}
          >
            {/* 엔티티 미선택 — empty placeholder */}
            {!selectedEntity && (
              <div style={{ padding: 'var(--spacing-10, 40px) var(--spacing-6)' }}>
                <EmptyState
                  title={t('changeHistoryViewer.empty.noEntity')}
                  description={t('changeHistoryViewer.empty.noEntityDescription')}
                />
              </div>
            )}

            {/* timeline loading skeleton */}
            {timelineLoading && <TimelineSkeleton rows={5} />}

            {/* timeline error */}
            {timelineError && selectedEntity && (
              <div style={{ padding: 'var(--spacing-6)' }}>
                <ErrorState
                  title={t('changeHistoryViewer.error.generic')}
                  message={timelineErrMsg}
                  onRetry={() => void timelineQuery.refetch()}
                />
              </div>
            )}

            {/* timeline empty */}
            {timelineEmpty && (
              <div style={{ padding: 'var(--spacing-6)' }}>
                <EmptyState
                  title={t('changeHistoryViewer.empty.noRevision')}
                  description={t('changeHistoryViewer.empty.noRevisionDescription')}
                />
              </div>
            )}

            {/* timeline default — revision 행들 */}
            {!timelineLoading && !timelineError && allRevisions.length > 0 &&
              allRevisions.map((item: RevisionTimelineItem) => {
                const isSelected = compareSelections.includes(item.revision);
                return (
                  <div
                    key={item.revision}
                    role="listitem"
                    aria-label={t('changeHistoryViewer.timeline.rowAriaLabel', { rev: item.revision })}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 160px 130px 70px 1fr 160px',
                      gap: 0,
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      alignItems: 'center',
                      background: isSelected
                        ? 'var(--color-brand-primary-subtle, rgba(98,0,238,0.05))'
                        : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        (e.currentTarget as HTMLDivElement).style.background =
                          'var(--color-surface-muted)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                    }}
                  >
                    {/* revision 번호 */}
                    <span
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        color: 'var(--color-brand-primary)',
                      }}
                    >
                      {item.revision}
                    </span>

                    {/* 시각 */}
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      {formatTime(item.revisionedAt)}
                    </span>

                    {/* actor */}
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.actor ?? t('changeHistoryViewer.common.unknown')}
                    </span>

                    {/* 유형 */}
                    <StatusPill tone={revTypeTone(item.type)}>
                      {t(`changeHistoryViewer.timeline.type.${item.type}`)}
                    </StatusPill>

                    {/* 요약 */}
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: '0 var(--spacing-2)',
                      }}
                    >
                      {item.summary ?? '—'}
                    </span>

                    {/* 액션 버튼들 */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--spacing-1)',
                        justifyContent: 'flex-end',
                        flexWrap: 'nowrap',
                      }}
                    >
                      {/* 비교용 선택 토글 */}
                      <button
                        type="button"
                        onClick={() => toggleCompareSelection(item.revision)}
                        aria-pressed={isSelected}
                        aria-label={`${t('changeHistoryViewer.action.selectForCompare')} rev ${item.revision}`}
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-2)',
                          background: isSelected
                            ? 'var(--color-brand-primary)'
                            : 'transparent',
                          color: isSelected
                            ? 'var(--color-text-inverse)'
                            : 'var(--color-text-secondary)',
                          border: `1px solid ${isSelected ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-family-sans)',
                          fontWeight: isSelected ? 600 : 400,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('changeHistoryViewer.action.compare')}
                      </button>

                      {/* 스냅샷 보기 */}
                      <button
                        type="button"
                        onClick={() => openSnapshot(item.revision)}
                        aria-label={`${t('changeHistoryViewer.action.viewSnapshot')} rev ${item.revision}`}
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-2)',
                          background: 'transparent',
                          color: 'var(--color-text-secondary)',
                          border: '1px solid var(--color-surface-border)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-xs)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-family-sans)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('changeHistoryViewer.action.viewSnapshot')}
                      </button>
                    </div>
                  </div>
                );
              })
            }

            {/* 타임라인 무한 스크롤 sentinel */}
            <div ref={timelineSentinelRef} style={{ height: 8 }} />
            {timelineQuery.isFetchingNextPage && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  padding: 'var(--spacing-3)',
                  textAlign: 'center',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t('changeHistoryViewer.timeline.loadingMore')}
              </div>
            )}
            {selectedEntity && !timelineQuery.hasNextPage && allRevisions.length > 0 && !timelineLoading && (
              <div
                style={{
                  padding: 'var(--spacing-3)',
                  textAlign: 'center',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  borderTop: '1px solid var(--color-surface-border)',
                }}
              >
                {t('changeHistoryViewer.timeline.end')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 공통 모듈 패널 */}
      <div
        style={{
          marginTop: 'var(--spacing-6)',
          padding: 'var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <p style={{ fontWeight: 600, marginBottom: 'var(--spacing-2)', color: 'var(--color-text-primary)' }}>
          {t('changeHistoryViewer.commonUsage.title')}
        </p>
        <ul style={{ margin: 0, paddingLeft: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <li>{t('changeHistoryViewer.commonUsage.c047')}</li>
          <li>{t('changeHistoryViewer.commonUsage.c048')}</li>
          <li>{t('changeHistoryViewer.commonUsage.c045')}</li>
        </ul>
      </div>

      {/* ── Drawers (dialog 상태) ── */}
      {diffDrawer && selectedEntity && (
        <RevisionDiffDrawer
          entityId={selectedEntity.id}
          fromRev={diffDrawer.fromRev}
          toRev={diffDrawer.toRev}
          onClose={() => setDiffDrawer(null)}
        />
      )}

      {snapshotDrawer !== null && selectedEntity && (
        <RevisionSnapshotDrawer
          entityId={selectedEntity.id}
          rev={snapshotDrawer}
          onClose={() => setSnapshotDrawer(null)}
        />
      )}

      {/* 반응형 + 애니메이션 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          .chv-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
